import { ApiError, ForbiddenError } from '@lib/api';

/**
 * The four refusals a component write has that the shared `WriteError` cannot
 * word usefully on its own.
 *
 * `WriteError` already handles a 403, a version conflict and the `issues` list a
 * `VALIDATION_FAILED` carries, and operators have learned the shape of those on
 * every other screen. This module covers only the failures whose *code* carries
 * a meaning the status does not — two of these are 409s that mean opposite
 * things — and returns `null` for everything else so the shared panel keeps
 * rendering the rest. A second wording of a 403 here would be a worse app, not
 * a more helpful one.
 *
 * Out here rather than inside the dialog because vitest runs on `node`: copy
 * written in a component is copy no test can see, and the sentence that matters
 * most below is the one an operator has never read before.
 */

export interface ComponentWriteCopy {
    /** The headline, naming the thing that was refused. */
    readonly title: string;
    /** The API's own sentence, which names the flag or the id at fault. */
    readonly body: string;
    /** What to do about it. Absent when the title and body already say it. */
    readonly detail?: string;
}

/**
 * ## Why the API's message is passed through rather than rebuilt
 *
 * The exception filter answers with `{ code, message }` and nothing else — no
 * `componentId`, no `key` field to read. The flag that was refused exists only
 * inside the sentence. Rebuilding that sentence from the draft is not possible
 * either: the operator submitted a list, and only the server knows which member
 * of it failed.
 *
 * So the API's wording is the body, and this module adds the headline and the
 * way out. Worth knowing for the sprint: if the filter ever echoes the offending
 * key as its own field, this can name it structurally instead of trusting prose.
 */
export function explainComponentWriteFailure(error: Error): ComponentWriteCopy | null {
    // A refusal is the app working, and `WriteError` says so in its own tone.
    if (!(error instanceof ApiError) || error instanceof ForbiddenError) return null;

    switch (error.code) {
        case 'COMPONENT_ALREADY_EXISTS':
            return {
                title: 'That component id is taken',
                body: error.message,
                // Not retryable by re-reading, and not fixable by overwriting:
                // the API refuses rather than overwrites so an existing
                // component's history is not rebased onto something else.
                detail: 'Pick another id. Ids are never reused, so nothing here can be overwritten.',
            };

        case 'COMPONENT_MEMBER_NOT_ENTITLEMENT':
            return {
                title: 'That flag cannot be a member',
                body: error.message,
                // The sentence this whole module exists for. `purpose` is the
                // membership criterion, not a label — an operator who has never
                // read that has no way to guess why a real flag was refused.
                detail: "A component groups entitlement flags only, so that a museum is never billed for a rollout toggle or a kill switch. Either change that flag's purpose to Entitlement on its own page, or remove it from this component.",
            };

        case 'FLAG_NOT_FOUND':
            return {
                title: 'One of those flags does not exist',
                body: error.message,
                // Kept apart from the purpose refusal for the reason the API
                // keeps them apart: an operator told "not an entitlement" about
                // a key they mistyped would go and reclassify some other flag.
                detail: 'Check the key for a typo. A flag has to exist in the catalog before a component can name it.',
            };

        case 'INVALID_COMPONENT_ID':
            return {
                title: 'That component id is not usable',
                body: error.message,
                detail: "Use 1-64 characters of A-Z, a-z, 0-9, '_' or '-'. Unlike a flag key, an id has no dots.",
            };

        default:
            return null;
    }
}
